/**
 * Names the payees of the expenses on screen by fetching exactly those ids (getPayeesByIds), so a
 * vendor's name never depends on the whole ~9,800-row payee table being loaded (PostgREST returns at
 * most 1,000 rows per request). Returns a lookup that gives "—" while loading or when the payee is not
 * readable by this user.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPayeesByIds } from "../services/finance.service";

export function useFinancePayeeNames(
  ids: (string | null | undefined)[],
): (id: string | null | undefined) => string {
  const key = useMemo(
    () =>
      Array.from(new Set(ids.filter((x): x is string => !!x)))
        .sort()
        .join(","),
    [ids],
  );
  const q = useQuery({
    // "finance-" prefix: Finance settings saves invalidate every finance-* query.
    queryKey: ["finance-payee-names", key],
    queryFn: () => getPayeesByIds(key ? key.split(",") : []),
    enabled: key !== "",
    staleTime: 60_000,
  });
  return useMemo(() => {
    const m = new Map((q.data ?? []).map((p) => [p.id, p.display_name]));
    return (id) => (id ? (m.get(id) ?? "—") : "—");
  }, [q.data]);
}
